import moment from 'moment';
import { CompositeModel } from './composite.model';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { ValueHelperInstanceToken } from '../../interfaces/value-helper.interface';
import { assignModelOptions } from '../../utilities/assign-model-options';
import { NotificationUtilities } from '../../utilities/notification-utilities';
import { NotificationModel } from '../low-level/notification.model';
import { ValueHelperModel } from '../low-level/value-helper.model';
import { TimeModelOptions } from '../options/time-model-options';
import { TextModel } from '../primitive/text.model';
import { TimeModel } from '../primitive/time.model';
import { DropdownModel } from './dropdown.model';
import { ODataObject } from '../../interfaces/odata-model.interface';
import { ConfigConstants } from '../../config/config-constants';
import { SHOW_APPROVER_TRANSITIONS, IMPOSSIBLE_TRANSITIONS } from './approver-transitions.constants';

export class CriticalityModel extends CompositeModel {
    public statusInArrival: DropdownModel<TextModel>;
    public approver: TextModel;
    public showApprover: boolean;
    public showWarning: boolean;
    public notificationEffectStatus: string;
    public selectedEffectStatus: string;
    public downTimeStartDateTime: TimeModel;
    public timeOfRecoveryDateTime: TimeModel;
    private isCentriCareContract: boolean;

    /**
     * Criticality model for storing criticality information.
     *
     * @param notification current notification data where criticality information is stored and needs to be updated
     */
    public constructor(private readonly notification: NotificationModel) {
        super('CriticalityModel');

        const injector = CustomInjector.getInjector();
        const valueHelperService = injector.get(ValueHelperInstanceToken);
        const notificationUtilities = injector.get(NotificationUtilities);

        this.isCentriCareContract = this.notification.isCentriCareContract();
        // Get the equipment status in arrival from the backend
        const equipmentStatusInArrival = notification.isCentriCareContract()
            ? valueHelperService.getCentriCareStatInArrival()
            : valueHelperService.getEquipmentStatusInArrival();

        // Create a dropdown model for the equipment status in arrival
        const equipmentStatusInArrivalEntries = equipmentStatusInArrival.map(
            (vh: ValueHelperModel) =>
                new DisplayDecorator<TextModel>(vh.descText, new TextModel('ArrivalStatus', vh.code)),
        );
        this.statusInArrival = <DropdownModel<TextModel>>(
            this.set(new DropdownModel<TextModel>('ArrivalStatus', equipmentStatusInArrivalEntries))
        );

        // Set the default value for the dropdown model
        const defaultStatusInArrival = this.notification.effect ? this.notification.effect.toString() : '';
        if (defaultStatusInArrival.length > 0) {
            this.statusInArrival.selectByValue(defaultStatusInArrival);
        }

        // Create a text model for the approver
        this.approver = <TextModel>this.set(new TextModel('Approver'));
        this.approver.options = {
            ...this.approver.options,
            required: true,
        };
        this.notificationEffectStatus = notificationUtilities.getNotificationStatusInArrivalDescription(notification);

        const now = moment().startOf('minute');
        const startMalfunction = notification.startMalfunction.isValid()
            ? notification.startMalfunction.clone()
            : moment.invalid();

        this.downTimeStartDateTime = <TimeModel>(
            this.set(new TimeModel('StartTime', startMalfunction, new TimeModelOptions({ maxValue: now.clone() })))
        );

        this.timeOfRecoveryDateTime = <TimeModel>this.set(
            new TimeModel(
                'EndTime',
                now.clone(),
                new TimeModelOptions({
                    minValue: this.downTimeStartDateTime.value.clone(),
                }),
            ),
        );
    }

    /**
     * Set the minimum value of the time of recovery to the start downtime.
     */
    public setRecoveryDateTimeOptions(): void {
        if (!this.downTimeStartDateTime.value.isValid()) {
            return;
        }

        this.timeOfRecoveryDateTime = assignModelOptions(
            this.timeOfRecoveryDateTime,
            {
                ...this.timeOfRecoveryDateTime.options,
                minValue: this.downTimeStartDateTime.value.clone().startOf('minute'),
            },
            this,
        );
    }

    /**
     * Update the visibility of the approver and warning based on the current transition.
     *
     * The current transition is determined by the notification effect and the selected status from the dropdown.
     * The transitions are divided into three lists: showApproverTransitions, hideApproverTransitions and impossibleTransitions.
     * If the current transition is in showApproverTransitions, the approver is shown.
     * If the current transition is in hideApproverTransitions, the approver will not be shown.
     * If the current transition is in impossibleTransitions, the warning is shown.
     * Otherwise, both the approver and warning are hidden.
     */
    public updateApproverAndWarningVisibility(): void {
        // Hide approver and warning both by default.
        this.showApprover = false;
        this.showWarning = false;

        // Determine the current transition based on notification effect and selected entry value.
        const transition: [string, string] = [
            this.notification.effect.toString() ?? '',
            this.statusInArrival.selectedEntry.value ?? '',
        ];

        // Check if the current transition is in the list of showApproverTransitions or hideApproverTransitions.
        if (
            this.isCentriCareContract &&
            SHOW_APPROVER_TRANSITIONS.some(([from, to]) => from === transition[0] && to === transition[1])
        ) {
            this.showApprover = true;
        } else if (IMPOSSIBLE_TRANSITIONS.some(([from, to]) => from === transition[0] && to === transition[1])) {
            this.showWarning = true;
        }

        this.selectedEffectStatus = this.statusInArrival.selectedEntry.displayName;
    }

    public getOutModel(): ODataCriticalityOutModel {
        const notificationNo = this.notification.identifier.getQmnum();
        const taskNo = this.notification.identifier.getManum();
        const frontId = this.notification.identifier.getFrontId();

        const longTextInfo: LongTextInfo[] = [
            {
                Notification: notificationNo,
                Task: taskNo,
                Tdid: 'COEC',
                Tdline: `${this.notification.effect}-${this.statusInArrival.selectedEntry.value}`,
            },
        ];

        if (this.showApprover) {
            longTextInfo.push({
                Notification: notificationNo,
                Task: taskNo,
                Tdid: 'APPR',
                Tdline: this.approver.value || '',
            });
        }

        const outModel: ODataCriticalityOutModel = {
            Notification: notificationNo,
            Task: taskNo,
            FrontId: frontId,
            StartDate: this.downTimeStartDateTime.value.format(ConfigConstants.formatDateNoTimezone),
            StartTime: this.downTimeStartDateTime.value.format(ConfigConstants.formatXSDTimeDuration),
            EndDate: this.timeOfRecoveryDateTime.value.format(ConfigConstants.formatDateNoTimezone),
            EndTime: this.timeOfRecoveryDateTime.value.format(ConfigConstants.formatXSDTimeDuration),
            LongTextInfo: longTextInfo,
        };

        return outModel;
    }
}

export interface ODataCriticalityOutModel extends ODataObject {
    Notification: string;
    Task: string;
    FrontId: string;
    StartDate: string;
    StartTime: string;
    EndDate: string;
    EndTime: string;
    LongTextInfo: LongTextInfo[];
}

export interface LongTextInfo extends ODataObject {
    Notification: string;
    Task: string;
    Tdid: string;
    Tdline: string;
}

export interface ODataCriticalityResponseModel extends ODataObject, ODataCriticalityOutModel {
    __metadata: {
        id: string;
        uri: string;
        type: string;
    };
}
